import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, IconButton, Card, CardContent, List, ListItem, 
  Divider, Dialog, DialogTitle, DialogContent, DialogActions, Button, Chip,
  Collapse
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { API_URLS } from '../config/api';
import { useCart } from '../contexts/CartContext';

function OrderDetailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const openNum = searchParams.get('openNum');
  const tableNumber = searchParams.get('table');
  const { getOrderDetailsForOpenNum } = useCart();
  
  const [orderDetails, setOrderDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [expandedOrders, setExpandedOrders] = useState({});
  const [printLoading, setPrintLoading] = useState(false);

  // 添加背景图片状态
  const [backgroundImageLoaded, setBackgroundImageLoaded] = useState(false);
  
  // 检测背景图片是否能够加载
  useEffect(() => {
    const img = new Image();
    const imagePath = `${process.env.PUBLIC_URL || ''}/images/background.jpg`;
    console.log('OrderDetailPage 尝试加载背景图片:', imagePath);
    
    img.onload = () => {
      console.log('OrderDetailPage 背景图片加载成功:', imagePath);
      setBackgroundImageLoaded(true);
    };
    img.onerror = () => {
      console.warn('OrderDetailPage 背景图片加载失败，使用fallback背景. 图片路径:', imagePath);
      setBackgroundImageLoaded(false);
    };
    img.src = imagePath;
  }, []);

  // 获取订单详情数据
  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!openNum) {
        navigate('/table', { replace: true });
        return;
      }

      // 先检查Context中是否有缓存数据
      const cachedDetails = getOrderDetailsForOpenNum(openNum);
      if (cachedDetails) {
        setOrderDetails(cachedDetails);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(`${API_URLS.SEARCH_ORDER_DETAIL}?openNum=${openNum}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        const data = await response.json();
        
        if (data.code === 200 && data.data) {
          setOrderDetails(data.data);
        } else {
          console.error('获取订单详情失败:', data.message);
          setOrderDetails([]);
        }
      } catch (error) {
        console.error('获取订单详情失败:', error);
        setOrderDetails([]);
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [openNum, navigate, getOrderDetailsForOpenNum]);

  // 按orderNum分组订单
  const getGroupedOrders = () => {
    const grouped = {};
    orderDetails.forEach(order => {
      const orderNum = order.orderNum;
      if (!grouped[orderNum]) {
        grouped[orderNum] = [];
      }
      grouped[orderNum].push(order);
    });
    return grouped;
  };

  // 计算单个订单组的总计
  const getOrderGroupTotal = (orderGroup) => {
    const totalItems = orderGroup.reduce((sum, item) => sum + item.number, 0);
    const totalPrice = orderGroup.reduce((sum, item) => sum + (item.price * item.number), 0);
    return { totalItems, totalPrice };
  };

  // 格式化价格
  const formatPrice = (price) => {
    return typeof price === 'number' ? price.toFixed(2) : '0.00';
  };

  // 处理订单行点击
  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setDetailDialogOpen(true);
  };

  // 关闭详情对话框
  const handleDetailDialogClose = () => {
    setDetailDialogOpen(false);
    setSelectedOrder(null);
  };

  // 切换订单组展开状态
  const toggleOrderExpansion = (orderNum) => {
    setExpandedOrders(prev => ({
      ...prev,
      [orderNum]: !prev[orderNum]
    }));
  };

  // 计算总计
  const getTotalInfo = () => {
    const totalItems = orderDetails.reduce((sum, item) => sum + item.number, 0);
    const totalPrice = orderDetails.reduce((sum, item) => sum + (item.price * item.number), 0);
    return { totalItems, totalPrice };
  };

  // 打印详单功能
  const handlePrintOrderDetail = async () => {
    if (!openNum || printLoading) return;
    
    try {
      setPrintLoading(true);
      const response = await fetch(`${API_URLS.PRINT_ORDER_DETAIL}?openNum=${openNum}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      const data = await response.json();
      
      if (data.code === 200) {
        // 打印成功提示
        alert('打印详单成功！');
      } else {
        console.error('打印详单失败:', data.message);
        alert('打印详单失败，请重试');
      }
    } catch (error) {
      console.error('打印详单失败:', error);
      alert('打印详单失败，请重试');
    } finally {
      setPrintLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        height: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: '#0a0f1c'
      }}>
        <Box sx={{
          width: '50px',
          height: '50px',
          borderRadius: '50%',
          border: '4px solid #ffc107',
          borderTop: '4px solid transparent',
          animation: 'spin 1s linear infinite',
          marginBottom: '24px'
        }} />
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </Box>
    );
  }

  const { totalItems, totalPrice } = getTotalInfo();
  const groupedOrders = getGroupedOrders();

  return (
    <Box sx={{ 
      minHeight: '100vh',
      backgroundColor: '#0a0f1c',
      backgroundImage: backgroundImageLoaded ? [
        `linear-gradient(rgba(5, 10, 20, 0.85), rgba(10, 15, 28, 0.85))`,
        `url('${process.env.PUBLIC_URL || ''}/images/background.jpg')`
      ].join(', ') : `linear-gradient(135deg, 
        rgba(5, 10, 20, 0.95) 0%, 
        rgba(10, 15, 28, 0.9) 25%,
        rgba(15, 25, 45, 0.85) 50%,
        rgba(20, 30, 50, 0.9) 75%,
        rgba(10, 15, 28, 0.95) 100%
      )`,
      backgroundSize: 'cover',
      backgroundPosition: 'center center',
      backgroundRepeat: 'no-repeat'
    }}>
      {/* 顶部导航栏 */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center',
        padding: '16px 20px',
        backgroundColor: 'rgba(26, 35, 50, 0.8)',
        backdropFilter: 'blur(15px)',
        borderBottom: '2px solid rgba(255, 193, 7, 0.3)'
      }}>
        <IconButton 
          onClick={() => navigate('/table')}
          sx={{ 
            mr: 2,
            color: '#ffc107',
            backgroundColor: 'rgba(255, 193, 7, 0.1)',
            '&:hover': {
              backgroundColor: 'rgba(255, 193, 7, 0.2)'
            }
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ 
            fontSize: 20, 
            fontWeight: 'bold', 
            color: '#ffc107',
            mb: 0.5
          }}>
            📋 订单详情
          </Typography>
          <Typography sx={{ 
            fontSize: 14, 
            color: '#e2e8f0'
          }}>
            {tableNumber && `桌号：${tableNumber} • `}开台号：{openNum}
          </Typography>
        </Box>
      </Box>

      {/* 主要内容区域 */}
      <Box sx={{ 
        padding: '20px',
        maxWidth: '800px',
        margin: '0 auto'
      }}>
        {/* 订单汇总信息 */}
        <Card sx={{
          backgroundColor: 'rgba(26, 35, 50, 0.7)',
          backdropFilter: 'blur(15px)',
          border: '2px solid #ffc107',
          borderRadius: '16px',
          marginBottom: '20px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6)'
        }}>
          <CardContent sx={{ padding: '20px' }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <Typography sx={{ 
                fontSize: 18, 
                fontWeight: 'bold', 
                color: '#ffc107'
              }}>
                🍸 订单汇总
              </Typography>
              <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <Chip
                  label={`${Object.keys(groupedOrders).length}个订单`}
                  sx={{
                    backgroundColor: '#ffc107',
                    color: '#0a0f1c',
                    fontWeight: 600
                  }}
                />
                <Button
                  onClick={handlePrintOrderDetail}
                  disabled={printLoading}
                  sx={{
                    backgroundColor: '#10b981',
                    color: '#fff',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: 600,
                    textTransform: 'none',
                    minWidth: '100px',
                    '&:hover': {
                      backgroundColor: '#059669'
                    },
                    '&:disabled': {
                      backgroundColor: '#6b7280',
                      color: '#9ca3af'
                    }
                  }}
                >
                  {printLoading ? '🖨️ 打印中...' : '🖨️ 打印详单'}
                </Button>
              </Box>
            </Box>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Typography sx={{ 
                fontSize: 16, 
                color: '#e2e8f0'
              }}>
                总计：{totalItems}件商品
              </Typography>
              <Typography sx={{ 
                fontSize: 20, 
                fontWeight: 'bold', 
                color: '#ffc107'
              }}>
                ¥{formatPrice(totalPrice)}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* 按订单号分组的订单列表 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {Object.entries(groupedOrders).map(([orderNum, orderGroup]) => {
            const { totalItems: groupItems, totalPrice: groupPrice } = getOrderGroupTotal(orderGroup);
            const isExpanded = expandedOrders[orderNum] || false;
            const firstOrderTime = orderGroup[0]?.createAt || '';
            
            return (
              <Card 
                key={orderNum}
                sx={{
                  backgroundColor: 'rgba(26, 35, 50, 0.7)',
                  backdropFilter: 'blur(15px)',
                  border: '1px solid rgba(255, 193, 7, 0.3)',
                  borderRadius: '16px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6)',
                  overflow: 'hidden'
                }}
              >
                {/* 订单头部 */}
                <Box
                  onClick={() => toggleOrderExpansion(orderNum)}
                  sx={{
                    padding: '16px 20px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    backgroundColor: 'rgba(255, 193, 7, 0.05)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 193, 7, 0.1)'
                    },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2,
                      marginBottom: '8px'
                    }}>
                                             <Typography sx={{ 
                         fontSize: 16, 
                         fontWeight: 'bold', 
                         color: '#ffc107'
                       }}>
                         📄 订单号：{orderNum}
                       </Typography>
                      <Chip
                        label={`${orderGroup.length}个商品`}
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(255, 193, 7, 0.2)',
                          color: '#ffc107',
                          fontSize: '12px'
                        }}
                      />
                    </Box>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <Typography sx={{ 
                        fontSize: 14, 
                        color: '#94a3b8'
                      }}>
                        {groupItems}件 • {firstOrderTime}
                      </Typography>
                      <Typography sx={{ 
                        fontSize: 18, 
                        fontWeight: 'bold', 
                        color: '#ffc107'
                      }}>
                        ¥{formatPrice(groupPrice)}
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton 
                    sx={{ 
                      color: '#ffc107',
                      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease'
                    }}
                  >
                    <ExpandMoreIcon />
                  </IconButton>
                </Box>

                {/* 订单详细商品列表 */}
                <Collapse in={isExpanded} timeout="auto">
                  <List sx={{ padding: 0 }}>
                    {orderGroup.map((order, index) => (
                      <React.Fragment key={order.id}>
                        <ListItem
                          onClick={() => handleOrderClick(order)}
                          sx={{
                            padding: '16px 20px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 193, 7, 0.1)'
                            },
                            '&:active': {
                              backgroundColor: 'rgba(255, 193, 7, 0.2)'
                            }
                          }}
                        >
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            width: '100%',
                            gap: 2
                          }}>
                            {/* 商品图标 */}
                            <Box sx={{
                              width: 40,
                              height: 40,
                              backgroundColor: 'rgba(255, 193, 7, 0.2)',
                              borderRadius: '10px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <RestaurantIcon sx={{ color: '#ffc107', fontSize: 20 }} />
                            </Box>

                            {/* 商品信息 */}
                            <Box sx={{ flex: 1 }}>
                              <Typography sx={{ 
                                fontSize: 14, 
                                fontWeight: 600, 
                                color: '#e2e8f0',
                                marginBottom: '4px'
                              }}>
                                {order.title}
                              </Typography>
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                gap: 2,
                                flexWrap: 'wrap'
                              }}>
                                <Typography sx={{ 
                                  fontSize: 12, 
                                  color: '#94a3b8'
                                }}>
                                  {order.number}{order.unit} × ¥{formatPrice(order.price)}
                                </Typography>
                                <Box sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center',
                                  gap: 0.5
                                }}>
                                  <AccessTimeIcon sx={{ 
                                    fontSize: 12, 
                                    color: '#94a3b8' 
                                  }} />
                                  <Typography sx={{ 
                                    fontSize: 11, 
                                    color: '#94a3b8'
                                  }}>
                                    {order.addTime?.split(' ')[1]}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>

                            {/* 小计金额 */}
                            <Typography sx={{ 
                              fontSize: 14, 
                              fontWeight: 'bold', 
                              color: '#ffc107'
                            }}>
                              ¥{formatPrice(order.price * order.number)}
                            </Typography>
                          </Box>
                        </ListItem>
                        {index < orderGroup.length - 1 && (
                          <Divider sx={{ 
                            borderColor: 'rgba(255, 193, 7, 0.1)',
                            marginLeft: '20px',
                            marginRight: '20px'
                          }} />
                        )}
                      </React.Fragment>
                    ))}
                  </List>
                </Collapse>
              </Card>
            );
          })}
        </Box>

        {/* 空状态 */}
        {Object.keys(groupedOrders).length === 0 && (
          <Card sx={{
            backgroundColor: 'rgba(26, 35, 50, 0.7)',
            backdropFilter: 'blur(15px)',
            border: '1px solid rgba(255, 193, 7, 0.3)',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6)'
          }}>
            <CardContent>
              <Box sx={{ 
                padding: '40px 20px',
                textAlign: 'center'
              }}>
                <Typography sx={{ 
                  fontSize: 16, 
                  color: '#94a3b8'
                }}>
                  暂无订单详情
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* 订单详情对话框 */}
      <Dialog
        open={detailDialogOpen}
        onClose={handleDetailDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(26, 35, 50, 0.95)',
            backdropFilter: 'blur(20px)',
            color: '#e2e8f0',
            borderRadius: '16px',
            border: '2px solid #ffc107',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.8)'
          }
        }}
      >
        <DialogTitle sx={{
          color: '#ffc107',
          fontSize: '18px',
          fontWeight: 600,
          textAlign: 'center',
          borderBottom: '1px solid rgba(255, 193, 7, 0.2)',
          paddingBottom: '16px'
        }}>
          🍸 商品详情
        </DialogTitle>
        <DialogContent sx={{ padding: '24px' }}>
          {selectedOrder && (
            <Box>
              <Typography sx={{ 
                fontSize: 20, 
                fontWeight: 'bold', 
                color: '#e2e8f0',
                marginBottom: '16px'
              }}>
                {selectedOrder.title}
              </Typography>
              
              <Box sx={{ marginBottom: '12px' }}>
                <Typography sx={{ 
                  fontSize: 14, 
                  color: '#94a3b8',
                  marginBottom: '4px'
                }}>
                  商品信息
                </Typography>
                <Box sx={{ 
                  backgroundColor: 'rgba(255, 193, 7, 0.1)',
                  padding: '12px',
                  borderRadius: '8px'
                }}>
                  <Typography sx={{ color: '#e2e8f0' }}>
                    商品ID：{selectedOrder.product}
                  </Typography>
                  <Typography sx={{ color: '#e2e8f0' }}>
                    数量：{selectedOrder.number}{selectedOrder.unit}
                  </Typography>
                  <Typography sx={{ color: '#e2e8f0' }}>
                    单价：¥{formatPrice(selectedOrder.price)}
                  </Typography>
                  <Typography sx={{ color: '#ffc107', fontWeight: 'bold' }}>
                    小计：¥{formatPrice(selectedOrder.price * selectedOrder.number)}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ marginBottom: '12px' }}>
                <Typography sx={{ 
                  fontSize: 14, 
                  color: '#94a3b8',
                  marginBottom: '4px'
                }}>
                  订单信息
                </Typography>
                <Box sx={{ 
                  backgroundColor: 'rgba(255, 193, 7, 0.1)',
                  padding: '12px',
                  borderRadius: '8px'
                }}>
                  <Typography sx={{ color: '#e2e8f0' }}>
                    订单号：{selectedOrder.orderNum}
                  </Typography>
                  <Typography sx={{ color: '#e2e8f0' }}>
                    订单ID：{selectedOrder.orderId}
                  </Typography>
                  <Typography sx={{ color: '#e2e8f0' }}>
                    开台号：{selectedOrder.openNum}
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Typography sx={{ 
                  fontSize: 14, 
                  color: '#94a3b8',
                  marginBottom: '4px'
                }}>
                  时间信息
                </Typography>
                <Box sx={{ 
                  backgroundColor: 'rgba(255, 193, 7, 0.1)',
                  padding: '12px',
                  borderRadius: '8px'
                }}>
                  <Typography sx={{ color: '#e2e8f0' }}>
                    添加时间：{selectedOrder.addTime}
                  </Typography>
                  <Typography sx={{ color: '#e2e8f0' }}>
                    创建时间：{selectedOrder.createAt}
                  </Typography>
                  <Typography sx={{ color: '#e2e8f0' }}>
                    更新时间：{selectedOrder.updateAt}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          justifyContent: 'center', 
          paddingBottom: '24px'
        }}>
          <Button
            onClick={handleDetailDialogClose}
            sx={{
              backgroundColor: '#ffc107',
              color: '#0a0f1c',
              borderRadius: '8px',
              padding: '10px 24px',
              fontSize: '14px',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#f59e0b'
              }
            }}
          >
            确认
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default OrderDetailPage; 